import { mergeSessionState, normalizeSessionState } from "@/client/lib/session-state";
import type { ActiveToolRun, ServerEvent, SessionState } from "@/shared/types";

function applyAssistantDelta(
  assistant: SessionState["activeAssistant"],
  event: Extract<ServerEvent, { type: "assistant-delta" }>,
): SessionState["activeAssistant"] {
  if (!assistant) {
    return assistant;
  }

  const blocks = [...assistant.blocks];
  const current = blocks[event.contentIndex];
  if (event.blockType === "text") {
    blocks[event.contentIndex] = {
      type: "text",
      text: current?.type === "text" ? current.text + event.delta : event.delta,
    };
  } else {
    blocks[event.contentIndex] = {
      type: "thinking",
      thinking: current?.type === "thinking" ? current.thinking + event.delta : event.delta,
    };
  }

  return { ...assistant, blocks };
}

function applyToolDelta(
  tools: ActiveToolRun[],
  event: Extract<ServerEvent, { type: "tool-delta" }>,
): ActiveToolRun[] {
  return tools.map((tool) => {
    if (tool.toolCallId !== event.toolCallId) {
      return tool;
    }

    const blocks = [...tool.blocks];
    for (const delta of event.deltas) {
      const current = blocks[delta.contentIndex];
      blocks[delta.contentIndex] =
        delta.blockType === "text"
          ? {
              type: "text",
              text: current?.type === "text" ? current.text + delta.delta : delta.delta,
            }
          : {
              type: "thinking",
              thinking: current?.type === "thinking" ? current.thinking + delta.delta : delta.delta,
            };
    }

    return {
      ...tool,
      blocks,
      details: event.details ?? tool.details,
    };
  });
}

function withEventRevision(state: SessionState, event: ServerEvent): SessionState {
  return {
    ...state,
    ...(typeof event.revision === "number" ? { revision: event.revision } : {}),
    ...(event.streamId ? { streamId: event.streamId } : {}),
  };
}

function mergeTools(previous: ActiveToolRun[], incoming: ActiveToolRun[]): ActiveToolRun[] {
  if (incoming.length === 0) {
    return [];
  }

  const byId = new Map(previous.map((tool) => [tool.toolCallId, tool]));
  for (const tool of incoming) {
    byId.set(tool.toolCallId, tool);
  }
  return [...byId.values()];
}

// HTTP snapshots cannot replace a newer SSE revision or cross the stream
// generation in which the mutation request was issued.
export function applySessionResponse(
  state: SessionState | undefined,
  incoming: SessionState,
  requestedStreamId: string | undefined,
): SessionState | undefined {
  if (state?.streamId !== requestedStreamId) return state;
  if (state?.streamId && incoming.streamId !== state.streamId) return state;
  return applyServerEvent(state, {
    type: "reset",
    state: incoming,
    revision: incoming.revision,
    streamId: incoming.streamId,
  });
}

export function applyServerEvent(
  state: SessionState | undefined,
  event: ServerEvent,
): SessionState | undefined {
  if (state) {
    const eventStreamId =
      event.streamId ?? (event.type === "reset" ? event.state.streamId : undefined);
    if (event.type === "reset") {
      if (
        eventStreamId === state.streamId &&
        typeof state.revision === "number" &&
        typeof event.revision === "number" &&
        event.revision < state.revision
      ) {
        return state;
      }
    } else if (
      (eventStreamId && state.streamId && eventStreamId !== state.streamId) ||
      (typeof state.revision === "number" &&
        typeof event.revision === "number" &&
        event.revision <= state.revision)
    ) {
      return state;
    }
  }

  if (event.type === "reset") {
    const merged = mergeSessionState(event.state, state);
    return merged ? withEventRevision(merged, event) : merged;
  }
  if (!state) {
    return state;
  }

  switch (event.type) {
    case "state":
      return normalizeSessionState(
        withEventRevision(
          {
            ...state,
            ...event.state,
          },
          event,
        ),
      );
    case "assistant":
      return normalizeSessionState(
        withEventRevision({ ...state, activeAssistant: event.assistant }, event),
      );
    case "assistant-delta":
      return normalizeSessionState(
        withEventRevision(
          {
            ...state,
            activeAssistant: applyAssistantDelta(state.activeAssistant, event),
          },
          event,
        ),
      );
    case "tools":
      return normalizeSessionState(
        withEventRevision(
          { ...state, activeTools: mergeTools(state.activeTools, event.tools) },
          event,
        ),
      );
    case "tool-delta":
      return normalizeSessionState(
        withEventRevision(
          { ...state, activeTools: applyToolDelta(state.activeTools, event) },
          event,
        ),
      );
    case "status":
      return normalizeSessionState(
        withEventRevision(
          {
            ...state,
            isStreaming: event.isStreaming,
            pendingMessageCount: event.pendingMessageCount,
          },
          event,
        ),
      );
    case "error":
      return state;
    default:
      return state;
  }
}
